import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import GatewayHeader from '../_gateway/GatewayHeader';
import GatewayFooter from '../_gateway/GatewayFooter';
import WelcomeModal from '../_gateway/WelcomeModal';
import WorldMap from '../_gateway/WorldMap';
import LogoCarousel from '../_gateway/LogoCarousel';
import useHomeInteractions from '../_gateway/useHomeInteractions';
import '../_gateway/gateway.css';

import { getWebsiteMetrics } from '_services/review-service';

import heroApron from '_assets/images/gateway/hero-apron.jpg';
import gatePassenger from '_assets/images/gateway/gate-passenger.jpg';
import gateAviation from '_assets/images/gateway/gate-aviation.jpg';

const DEFAULT_METRICS = {
  bookings: 7757542,
  airports: 30,
  airlines: 64,
  users: 54962,
  reviews: 5830,
  surveyAnswers: 3712,
};

const TICKER_ITEMS = [
  { key: 'bookings', label: 'Assistance Requests Complete' },
  { key: 'airports', label: 'International + Domestic Airports' },
  { key: 'airlines', label: 'Airlines' },
  { key: 'users', label: 'Passenger Mobile Downloads' },
  { key: 'reviews', label: 'Passenger Reviews Complete' },
  { key: 'surveyAnswers', label: 'Passenger Surveys Complete' },
];

const numberFormatter = new Intl.NumberFormat('en-GB');

const stageDotStyle = {
  width: 64,
  height: 64,
  flex: '0 0 auto',
  display: 'grid',
  placeItems: 'center',
  borderRadius: '50%',
  background: '#fff',
  boxShadow: '0 6px 18px rgba(9,26,54,0.22)',
  opacity: 0.62,
  transform: 'scale(.76)',
  transition: 'opacity .45s ease, transform .5s cubic-bezier(.2,1.5,.4,1), box-shadow .45s ease',
};
const stageLabelStyle = {
  fontSize: 12.5,
  fontWeight: 700,
  lineHeight: 1.25,
  letterSpacing: '.1em',
  textTransform: 'uppercase',
  color: '#1c2b4a',
  textAlign: 'center',
  opacity: 0.58,
  transition: 'opacity .45s ease',
  textShadow: '0 1px 7px rgba(255,255,255,0.9)',
};
const stageLiStyle = (left, top) => ({
  position: 'absolute',
  left,
  top,
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  width: 132,
});

const HERO_STAGES = [
  {
    stage: '0', left: '8.3%', top: '22%', label: 'Passenger request',
    icon: (<svg viewBox="0 0 24 24" width="29" height="29" style={{ color: '#1c2b4a' }} aria-hidden="true"><circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M4 20c0-4 4-6 8-6s8 2 8 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>),
  },
  {
    stage: '0.205', left: '25.8%', top: '43%', label: 'AI demand forecast',
    icon: (<svg viewBox="0 0 24 24" width="29" height="29" style={{ color: '#1c2b4a' }} aria-hidden="true"><rect x="3" y="4" width="18" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M8 20h8M12 16v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><path d="M7 13V9M11 13V7M15 13v-2M19 13V8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>),
  },
  {
    stage: '0.394', left: '42%', top: '72%', label: 'Resource planning',
    icon: (<svg viewBox="0 0 24 24" width="29" height="29" style={{ color: '#1c2b4a' }} aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><circle cx="15" cy="15" r="4" fill="none" stroke="currentColor" strokeWidth="1.6" /><path d="M15 13v2l1.4 1.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>),
  },
  {
    stage: '0.593', left: '58.9%', top: '72%', label: 'Live operations',
    icon: (<svg viewBox="0 0 24 24" width="29" height="29" style={{ color: '#1c2b4a' }} aria-hidden="true"><rect x="7" y="2" width="10" height="20" rx="2" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M11 19h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>),
  },
  {
    stage: '0.798', left: '76.4%', top: '43%', label: 'Assistance delivery',
    icon: (<svg viewBox="0 0 24 24" width="29" height="29" style={{ color: '#1c2b4a' }} aria-hidden="true"><circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M4 20c0-4 4-6 8-6s8 2 8 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><path d="M11 14l1 2 1-2-1 6-1-6z" fill="currentColor" /></svg>),
  },
  {
    stage: '1', left: '93.6%', top: '15%', label: 'Performance insights',
    icon: (<svg viewBox="0 0 24 24" width="29" height="29" style={{ color: '#1c2b4a' }} aria-hidden="true"><path fill="currentColor" d="M21,16V14L13,9V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V9L2,14V16L10,13.5V19L8,20.5V22L11.5,21L15,22V20.5L13,19V13.5L21,16Z" /></svg>),
  },
];

const JOURNEY_STOPS_A = [
  { cx: 70, textY: 72, label: 'PASSENGER' },
  { cx: 202, textY: 146, label: 'REQUEST' },
];
const JOURNEY_STOPS_B = [
  { cx: 467, textY: 146, label: 'FORECAST' },
  { cx: 600, textY: 72, label: 'PLANNING' },
  { cx: 732, textY: 146, label: 'LIVE OPS' },
  { cx: 865, textY: 72, label: 'AGENT' },
  { cx: 997, textY: 146, label: 'AIRCRAFT' },
];
const renderJourneyStop = (s) => (
  <g key={s.label}>
    <circle cx={s.cx} cy="104" r="13" fill="#fff" stroke="#208aef" strokeWidth="2" strokeOpacity=".28" data-ring="1" opacity=".28" />
    <circle cx={s.cx} cy="104" r="5" fill="#208aef" opacity=".5" data-dot="1" />
    <text x={s.cx} y={s.textY} textAnchor="middle" fill="#1c2b4a" fontFamily="Outfit, system-ui, sans-serif" fontSize="17" fontWeight="700" letterSpacing="1">{s.label}</text>
  </g>
);

const JOURNEY_LIST = ['Passenger', 'Request', 'AI intelligence', 'Forecast', 'Planning', 'Live operations', 'Agent', 'Aircraft', 'Performance & reporting'];

const searchIcon = (size) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"><circle cx="17" cy="5" r="2" fill="currentColor" /><path d="M15 8v3l2 5h4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><path d="M11 11h6l1 3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><path d="M11 11l-2 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="9" cy="17" r="5" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>
);
const planeIcon = (size) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"><path fill="currentColor" d="M21,16V14L13,9V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V9L2,14V16L10,13.5V19L8,20.5V22L11.5,21L15,22V20.5L13,19V13.5L21,16Z" /></svg>
);

const Home = () => {
  const pageRef = useRef(null);
  const location = useLocation();
  const [metrics, setMetrics] = useState(DEFAULT_METRICS);

  useHomeInteractions(pageRef);

  useEffect(() => {
    let mounted = true;
    getWebsiteMetrics()
      .then((data) => {
        if (mounted && data) setMetrics({ ...DEFAULT_METRICS, ...data });
      })
      .catch((error) => console.error('Error loading website metrics', error));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!location.hash || !pageRef.current) return;
    const el = pageRef.current.querySelector(location.hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  return (
    <div className="gw-page">
      <WelcomeModal />
      <GatewayHeader />

      <main id="main" ref={pageRef}>
        <div id="gw-stack" style={{ position: 'relative' }}>

          <section id="gw-hero-grid" aria-labelledby="hero-title" style={{ position: 'sticky', top: 0, zIndex: 1, minHeight: '100vh', maxHeight: '100vh', overflow: 'hidden', background: '#eaf2fd' }}>
            <div id="gw-hero-photo" style={{ position: 'absolute', inset: 0 }}>
              <img id="gw-hero" data-parallax="0.07" src={heroApron} alt="Aircraft at the gate with jet bridge and ground crew — wide apron crop" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </div>

            <div aria-hidden="true" id="gw-hero-veil" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(100deg, rgba(255,255,255,0.86) 0%, rgba(255,255,255,0.7) 22%, rgba(255,255,255,0.34) 40%, rgba(255,255,255,0.08) 56%, rgba(255,255,255,0) 70%)' }} />

            <div id="gw-hero-copy" style={{ position: 'relative', zIndex: 2, height: '100vh', maxWidth: 1240, margin: '0 auto', padding: '104px 32px 40%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <p style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 22px', fontSize: 13, fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', color: '#1670c8' }}>
                <span aria-hidden="true" style={{ fontSize: 15 }}>→</span> One connected journey
              </p>
              <h1 id="hero-title" style={{ margin: 0, maxWidth: '15em', fontSize: 'clamp(38px, 4.6vw, 68px)', lineHeight: 0.98, fontWeight: 800, letterSpacing: '-0.025em', textTransform: 'uppercase', color: '#1c2b4a' }}>
                Connecting every journey.<br /><span style={{ color: '#208aef' }}>Powering every operation.</span>
              </h1>
              <p style={{ margin: '28px 0 0', maxWidth: '22em', fontSize: 19, lineHeight: 1.6, fontWeight: 400, color: '#374151', fontFamily: 'var(--gw-font-sans)' }}>
                One intelligent platform connecting passengers, airports, airlines and assistance providers across accessible air travel.
              </p>
              <Link to="/request-demo" className="gw-cta-btn" style={{ marginTop: 30, display: 'inline-flex', alignItems: 'center', gap: 10, alignSelf: 'flex-start', minHeight: 52, padding: '15px 30px', borderRadius: 12, background: '#1670c8', color: '#fff', fontSize: 15, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', boxShadow: '0 10px 24px rgba(32,138,239,0.32)' }}>
                Book a demo
                <span aria-hidden="true" className="gw-arrow">→</span>
              </Link>
            </div>

            <div id="gw-hero-path" style={{ position: 'absolute', left: 0, right: 0, bottom: '8%', height: '25%', zIndex: 3 }}>
              <svg viewBox="0 0 1440 260" preserveAspectRatio="none" aria-hidden="true" focusable="false" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                <path d="M120 58 L372 112 L604 188 L848 188 L1100 112 L1348 40" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinejoin="round" strokeOpacity=".3" />
                <path id="gw-path-glow" d="M120 58 L372 112 L604 188 L848 188 L1100 112 L1348 40" fill="none" stroke="#8cc6ff" strokeWidth="8" strokeLinejoin="round" strokeOpacity=".35" style={{ filter: 'blur(5px)' }} />
                <path id="gw-path-draw" d="M120 58 L372 112 L604 188 L848 188 L1100 112 L1348 40" fill="none" stroke="#ffffff" strokeWidth="2.6" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
              <ol style={{ position: 'absolute', inset: 0, listStyle: 'none', margin: 0, padding: 0 }}>
                {HERO_STAGES.map((s) => (
                  <li key={s.label} data-stage={s.stage} style={stageLiStyle(s.left, s.top)}>
                    <span aria-hidden="true" data-stage-dot="1" style={stageDotStyle}>{s.icon}</span>
                    <span data-stage-label="1" style={stageLabelStyle}>{s.label}</span>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          <div id="gw-hero-runway" aria-hidden="true" style={{ height: '85vh' }} />

          <section id="gw-audience" aria-labelledby="audience-title" style={{ position: 'relative', zIndex: 2, background: '#fff', margin: 0, padding: '92px 0 8px', borderRadius: '32px 32px 0 0', boxShadow: '0 -24px 60px rgba(28,43,74,0.12)' }}>
            <div id="gw-audience-inner" style={{ maxWidth: 1240, margin: '0 auto', padding: '0 32px' }}>
              <span id="audience" aria-hidden="true" tabIndex={-1} style={{ display: 'block', position: 'relative', top: -100 }} />
              <div data-reveal="true" style={{ textAlign: 'center', maxWidth: '40em', margin: '0 auto 44px' }}>
                <p style={{ display: 'inline-flex', alignItems: 'center', gap: 10, margin: '0 0 16px', fontSize: 12, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#1670c8' }}>
                  <span aria-hidden="true">→</span> Choose your experience
                </p>
                <h2 id="audience-title" style={{ margin: 0, fontSize: 'clamp(26px, 2.6vw, 40px)', lineHeight: 1.02, fontWeight: 800, letterSpacing: '-0.02em', textTransform: 'uppercase', color: '#1c2b4a' }}>Which brings you here?</h2>
              </div>

              <div id="gw-audience-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 28, alignItems: 'stretch' }}>

                <Link data-reveal="true" to="/passengers" aria-label="For passengers: I'm travelling. Explore Passenger Assist." className="gw-gate gw-gate-light" style={{ boxShadow: '0 10px 30px rgba(28,43,74,0.07)', display: 'flex', flexDirection: 'column', borderRadius: 26, overflow: 'hidden', background: '#f4f8fe', border: '1px solid rgba(32,138,239,0.16)', color: '#1c2b4a', transition: 'transform .35s cubic-bezier(.2,.8,.2,1), box-shadow .35s ease' }}>
                  <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, background: '#1c2b4a' }}>
                    <div style={{ flex: '0 0 auto', width: 108, display: 'grid', placeItems: 'center', padding: '18px 0', background: '#208aef' }}>
                      <span aria-hidden="true" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', color: '#eaf2fd' }}>Gate</span>
                      <span aria-hidden="true" style={{ fontSize: 40, lineHeight: 1, fontWeight: 800, letterSpacing: '-0.03em', color: '#fff' }}>01</span>
                    </div>
                    <div style={{ flex: '1 1 auto', minWidth: 0, padding: '16px 22px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 7 }}>
                      <p style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#7fbcff' }}>
                        <span aria-hidden="true">→</span> For passengers
                      </p>
                      <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12, fontSize: 15, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: '#fff' }}>
                        <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: '50%', background: '#7fbcff', animation: 'gwGatePulse 2.4s ease-in-out infinite' }} />
                        A smoother journey
                      </p>
                    </div>
                    <div aria-hidden="true" style={{ flex: '0 0 auto', width: 62, display: 'grid', placeItems: 'center', borderLeft: '1px solid rgba(127,188,255,0.26)' }}>
                      <span style={{ color: '#7fbcff' }}>{searchIcon(28)}</span>
                    </div>
                  </div>

                  <div style={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', overflow: 'hidden', background: '#eaf2fd' }}>
                    <img src={gatePassenger} alt="Passenger in a wheelchair using the PRM Assist app at the gate" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </div>

                  <div style={{ padding: '32px 30px 34px', display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
                    <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 16, fontSize: 'clamp(26px, 2.2vw, 36px)', lineHeight: 1, fontWeight: 800, letterSpacing: '-0.02em', textTransform: 'uppercase', color: '#1c2b4a' }}>
                      <span style={{ color: '#1670c8' }}>{searchIcon(32)}</span>
                      I’m travelling
                    </h3>
                    <p style={{ margin: '18px 0 0', fontSize: 17, lineHeight: 1.6, color: '#374151', fontFamily: 'var(--gw-font-sans)' }}>
                      Request assistance, manage your journey and stay connected with the support you need from airport to aircraft.
                    </p>
                    <span style={{ margin: '28px 0 0', paddingTop: 22, borderTop: '1px dashed rgba(32,138,239,0.35)', display: 'inline-flex', alignItems: 'center', gap: 12, minHeight: 44, fontSize: 15, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: '#1670c8' }}>
                      Explore Passenger Assist
                      <span aria-hidden="true" className="gw-arrow">→</span>
                    </span>
                  </div>
                </Link>

                <Link data-reveal="true" to="/request-demo" aria-label="For aviation teams: I work in aviation. Book a demo." className="gw-gate gw-gate-dark" style={{ boxShadow: '0 10px 30px rgba(28,43,74,0.07)', display: 'flex', flexDirection: 'column', borderRadius: 26, overflow: 'hidden', background: '#1c2b4a', border: '1px solid #1c2b4a', color: '#ffffff', transition: 'transform .35s cubic-bezier(.2,.8,.2,1), box-shadow .35s ease' }}>
                  <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, background: '#1c2b4a' }}>
                    <div style={{ flex: '0 0 auto', width: 108, display: 'grid', placeItems: 'center', padding: '18px 0', background: '#208aef' }}>
                      <span aria-hidden="true" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', color: '#eaf2fd' }}>Gate</span>
                      <span aria-hidden="true" style={{ fontSize: 40, lineHeight: 1, fontWeight: 800, letterSpacing: '-0.03em', color: '#fff' }}>02</span>
                    </div>
                    <div style={{ flex: '1 1 auto', minWidth: 0, padding: '16px 22px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 7 }}>
                      <p style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#7fbcff' }}>
                        <span aria-hidden="true">→</span> For aviation teams
                      </p>
                      <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12, fontSize: 15, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: '#fff' }}>
                        <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: '50%', background: '#7fbcff', animation: 'gwGatePulse 2.4s ease-in-out infinite' }} />
                        Smarter operations
                      </p>
                    </div>
                    <div aria-hidden="true" style={{ flex: '0 0 auto', width: 62, display: 'grid', placeItems: 'center', borderLeft: '1px solid rgba(127,188,255,0.26)' }}>
                      <span style={{ color: '#7fbcff' }}>{planeIcon(28)}</span>
                    </div>
                  </div>

                  <div style={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', overflow: 'hidden', background: '#16233d' }}>
                    <img src={gateAviation} alt="Operations team member reviewing PRM Assist forecasting dashboards across multiple screens" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </div>

                  <div style={{ padding: '32px 30px 34px', display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
                    <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 16, fontSize: 'clamp(26px, 2.2vw, 36px)', lineHeight: 1, fontWeight: 800, letterSpacing: '-0.02em', textTransform: 'uppercase', color: '#ffffff' }}>
                      <span style={{ color: '#7fbcff' }}>{planeIcon(32)}</span>
                      I work in aviation
                    </h3>
                    <p style={{ margin: '18px 0 0', fontSize: 17, lineHeight: 1.6, color: '#cdd9ee', fontFamily: 'var(--gw-font-sans)' }}>
                      AI-powered technology for airports, airlines and service providers to predict demand, optimise resources and manage PRM operations in real time.
                    </p>
                    <span style={{ margin: '28px 0 0', paddingTop: 22, borderTop: '1px dashed rgba(127,188,255,0.35)', display: 'inline-flex', alignItems: 'center', gap: 12, minHeight: 44, fontSize: 15, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: '#7fbcff' }}>
                      Book a demo
                      <span aria-hidden="true" className="gw-arrow">→</span>
                    </span>
                  </div>
                </Link>

              </div>
            </div>
          </section>

        </div>

        <LogoCarousel />

        <section id="gw-journey" aria-labelledby="journey-title" style={{ maxWidth: 1240, margin: '52px auto 0', padding: '0 32px' }}>
          <span id="journey" aria-hidden="true" tabIndex={-1} style={{ display: 'block', position: 'relative', top: -90 }} />
          <div id="gw-journey-panel" data-reveal="true" style={{ borderRadius: 32, background: '#f4f8fe', border: '1px solid rgba(32,138,239,0.14)', padding: '34px 38px 26px', boxShadow: '0 10px 30px rgba(28,43,74,0.07)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap', marginBottom: 14 }}>
              <div>
                <p style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 10px', fontSize: 12, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#1670c8' }}>
                  <span aria-hidden="true">→</span> The connected journey
                </p>
                <h2 id="journey-title" style={{ margin: 0, fontSize: 'clamp(26px, 2.6vw, 38px)', lineHeight: 1.02, fontWeight: 800, letterSpacing: '-0.02em', textTransform: 'uppercase', color: '#1c2b4a' }}>
                  Every stage, on one platform.
                </h2>
              </div>
              <p style={{ margin: 0, maxWidth: '30em', fontSize: 16, lineHeight: 1.6, color: '#374151', fontFamily: 'var(--gw-font-sans)' }}>
                From the moment a passenger requests assistance, PRM Assist connects the data, the forecast, the roster and the agent on the ground — then feeds performance back into the next plan.
              </p>
            </div>

            <svg id="gw-journey-svg" viewBox="0 14 1200 142" width="100%" aria-hidden="true" focusable="false" style={{ display: 'block', overflow: 'visible' }}>
              <line x1="70" y1="104" x2="1130" y2="104" stroke="#208aef" strokeOpacity=".18" strokeWidth="2" />
              <line id="gw-line-progress" x1="70" y1="104" x2="1130" y2="104" stroke="#208aef" strokeWidth="3" strokeLinecap="round" strokeDasharray="1060" strokeDashoffset="1060" />

              {JOURNEY_STOPS_A.map(renderJourneyStop)}

              <g>
                <circle cx="335" cy="104" r="19" fill="#1c2b4a" />
                <circle cx="335" cy="104" r="19" fill="none" stroke="#208aef" strokeWidth="2" data-ring="1" opacity=".28" />
                <text x="335" y="109" textAnchor="middle" fill="#fff" fontFamily="Outfit, system-ui, sans-serif" fontSize="13" fontWeight="800" letterSpacing="0.6">AI</text>
                <text x="335" y="66" textAnchor="middle" fill="#1c2b4a" fontFamily="Outfit, system-ui, sans-serif" fontSize="17" fontWeight="700" letterSpacing="1">INTELLIGENCE</text>
              </g>

              {JOURNEY_STOPS_B.map(renderJourneyStop)}

              <g>
                <circle cx="1130" cy="104" r="13" fill="#fff" stroke="#208aef" strokeWidth="2" strokeOpacity=".28" data-ring="1" opacity=".28" />
                <circle cx="1130" cy="104" r="5" fill="#208aef" opacity=".5" data-dot="1" />
                <text x="1130" y="52" textAnchor="middle" fill="#1c2b4a" fontFamily="Outfit, system-ui, sans-serif" fontSize="17" fontWeight="700" letterSpacing="1">
                  <tspan x="1130" dy="0">PERFORMANCE</tspan>
                  <tspan x="1130" dy="20">&amp; REPORTING</tspan>
                </text>
              </g>

              <g id="gw-plane" aria-hidden="true" transform="translate(0 0)">
                <g transform="translate(70, 104)">
                  <circle r="18" fill="#f4f8fe" />
                  <path d="M21,16V14L13,9V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V9L2,14V16L10,13.5V19L8,20.5V22L11.5,21L15,22V20.5L13,19V13.5L21,16Z" fill="#1c2b4a" transform="rotate(90) scale(1.35) translate(-12,-12)" />
                </g>
              </g>
            </svg>

            <ol id="gw-journey-list" aria-label="Stages of the connected journey" style={{ position: 'absolute', width: 1, height: 1, margin: -1, overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap', listStyle: 'none', padding: 0, gap: 2 }}>
              {JOURNEY_LIST.map((label, idx) => (
                <li key={label} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0', borderTop: idx === 0 ? 'none' : '1px solid rgba(32,138,239,0.16)' }}>
                  <span aria-hidden="true" style={{ width: 11, height: 11, borderRadius: '50%', background: label === 'AI intelligence' ? '#1c2b4a' : '#208aef', flex: '0 0 auto' }} />
                  <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: '#1c2b4a' }}>{label}</span>
                </li>
              ))}
            </ol>

            <div style={{ marginTop: 14, paddingTop: 16, borderTop: '1px solid rgba(32,138,239,0.16)', display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              <p style={{ margin: 0, fontSize: 15, color: '#6b7280', fontFamily: 'var(--gw-font-sans)' }}>Predict &nbsp;·&nbsp; Plan &nbsp;·&nbsp; Allocate &nbsp;·&nbsp; Assist &nbsp;·&nbsp; Measure &nbsp;·&nbsp; Improve</p>
              <a href="#audience" style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}>Explore the platform →</a>
            </div>
          </div>
        </section>

        <section id="gw-network" aria-labelledby="network-title" style={{ maxWidth: 1240, margin: '96px auto 0', padding: '0 32px' }}>
          <div id="gw-network-grid" data-reveal="true" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.84fr) minmax(0, 1.16fr)', gap: 56, alignItems: 'center' }}>
            <div>
              <p style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 16px', fontSize: 12, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#1670c8' }}>
                <span aria-hidden="true">→</span> Our network
              </p>
              <h2 id="network-title" style={{ margin: 0, fontSize: 'clamp(26px, 2.6vw, 40px)', lineHeight: 1.02, fontWeight: 800, letterSpacing: '-0.02em', textTransform: 'uppercase', color: '#1c2b4a' }}>
                Book assistance at airports worldwide.
              </h2>
              <p style={{ margin: '22px 0 0', maxWidth: '30em', fontSize: 17, lineHeight: 1.6, color: '#374151', fontFamily: 'var(--gw-font-sans)' }}>
                Passengers create assistance bookings with airports for every journey, in one place. Airports, airlines and service providers see those requests months in advance.
              </p>
            </div>
            <div>
              <div style={{ borderRadius: 28, background: '#f4f8fe', border: '1px solid rgba(32,138,239,0.14)', padding: '22px 22px 12px', boxShadow: '0 10px 30px rgba(28,43,74,0.07)' }}>
                <WorldMap />
              </div>
            </div>
          </div>

          <ul id="gw-ticker" style={{ margin: '56px 0 0', padding: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '40px 48px' }}>
            {TICKER_ITEMS.map((item) => (
              <li key={item.key} data-reveal="true" style={{ padding: '26px 0 0', borderTop: '1px solid rgba(32,138,239,0.2)' }}>
                <p style={{ margin: 0, display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 'clamp(36px, 3.6vw, 54px)', lineHeight: 1, fontWeight: 800, letterSpacing: '-0.035em', color: '#1c2b4a' }}>
                  <span data-ticker={metrics[item.key]}>{numberFormatter.format(metrics[item.key])}</span><span style={{ color: '#208aef' }}>+</span>
                </p>
                <p style={{ margin: '14px 0 0', fontSize: 16, fontWeight: 600, letterSpacing: '.01em', color: '#374151', fontFamily: 'var(--gw-font-sans)' }}>{item.label}</p>
              </li>
            ))}
          </ul>
        </section>
      </main>

      <GatewayFooter />
    </div>
  );
};

export default Home;
